import { Button } from '@mui/material';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { ETranscoderProfileKind, TranscoderProfile } from '@vxui/common';
import { DeleteRowButton, Modal } from '@vxui/components';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkDeleteProfileModal, BulkDeleteProfileModalConfig } from './BulkDeleteProfileModal';
import { SingleDeleteProfileModal } from './SingleDeleteProfileModal';

type ActionModalComponent = {
  singleDelete: ReactElement;
  bulkDelete: ReactElement;
};

type Modal = {
  toggledClearRows: boolean;
  selectedProfiles: TranscoderProfile[];
  onSelectedRowsChangeHandler: ({
    selectedRows,
  }: {
    allSelected: boolean;
    selectedCount: number;
    selectedRows: TranscoderProfile[];
  }) => void;
  components: ActionModalComponent;
  subHeaderActions: ReactElement[];
};

export type TMutationTrigger = MutationTrigger<
  MutationDefinition<
    // eslint-disable-next-line
    any,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, never>, Record<string, never>>,
    string,
    // eslint-disable-next-line
    any,
    string
  >
>;

type UseActionModalProps = {
  kind: ETranscoderProfileKind;
  profileToDelete: TranscoderProfile | undefined;
  deleteAction: TMutationTrigger;
  onCloseDeleteModalHandler: () => void;
};

export const useActionModal = ({
  kind,
  profileToDelete,
  deleteAction,
  onCloseDeleteModalHandler,
}: UseActionModalProps): Modal => {
  const { t } = useTranslation();
  const [bulkDeleteModal, setBulkDeleteModal] = useState<BulkDeleteProfileModalConfig>({
    open: false,
  });
  const [toggledClearRows, setToggledClearRows] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<TranscoderProfile[]>([]);

  const onSelectedRowsChangeHandler = ({
    selectedRows,
  }: {
    allSelected: boolean;
    selectedCount: number;
    selectedRows: TranscoderProfile[];
  }) => setSelectedProfiles(selectedRows);

  const finishBulkAction = (): void => {
    setSelectedProfiles([]);
    setToggledClearRows(true);
    onConfirmModalCloseHandler();
    setTimeout(() => {
      setToggledClearRows(false);
    }, 300);
  };

  const onDeleteActionConfirmHandler = (): void => {
    const idsToDelete: string[] = selectedProfiles
      .filter(profile => profile.templates?.length === 0)
      .map((profile: TranscoderProfile) => profile.id);
    for (const id of idsToDelete) {
      deleteAction(id);
    }
    finishBulkAction();
  };

  const onDeleteActionHandler = () => {
    setBulkDeleteModal({
      open: true,
      saveBtn: (
        <Button key="delete" size="medium" variant="contained" color="error" onClick={onDeleteActionConfirmHandler}>
          {t('button.delete')}
        </Button>
      ),
    });
  };

  const onConfirmModalCloseHandler = () => {
    setBulkDeleteModal((oldVal: BulkDeleteProfileModalConfig) => ({ ...oldVal, open: false }));
  };

  const clearSelectableRowsHandler = () => {
    setSelectedProfiles([]);
    setToggledClearRows(true);
    setTimeout(() => {
      setToggledClearRows(false);
    }, 300);
  };

  const subHeaderActions = [<DeleteRowButton key="delete-button" onDeleteActionHandler={onDeleteActionHandler} />];

  return {
    toggledClearRows,
    selectedProfiles,
    onSelectedRowsChangeHandler,
    subHeaderActions,
    components: {
      singleDelete: (
        <SingleDeleteProfileModal
          profile={profileToDelete}
          kind={kind}
          deleteAction={deleteAction}
          onClose={onCloseDeleteModalHandler}
          clearSelectableRows={clearSelectableRowsHandler}
        />
      ),
      bulkDelete: (
        <BulkDeleteProfileModal
          open={bulkDeleteModal.open}
          saveBtn={bulkDeleteModal.saveBtn}
          kind={kind}
          selectedProfiles={selectedProfiles}
          onClose={onConfirmModalCloseHandler}
        />
      ),
    },
  };
};
